import { useEffect, useState } from 'react';

export interface ProgressEvent {
  ts: number;
  job_id: string;
  level: 'info' | 'warn' | 'error' | 'success';
  phase: string;
  message: string;
  detail?: Record<string, unknown>;
}

/**
 * Shared SSE subscription hook for a recon job's progress stream.
 *
 * Multiple components on the same page (the activity feed, the chunks
 * panel, future widgets) can call this hook with the same jobId — React
 * will subscribe each independently. To save bandwidth, this hook uses
 * the browser's automatic EventSource connection pooling: same-origin
 * GETs to the same URL share a connection.
 *
 * Returns:
 *   - events: chronological list of every progress event seen
 *   - connected: true while the SSE connection is open
 *   - done: true after we've seen a job.complete / job.failed event
 *
 * Cleans up on unmount.
 */
export function useJobProgressStream(
  jobId: string,
  apiBaseUrl: string,
  onTerminal?: () => void,
): { events: ProgressEvent[]; connected: boolean; done: boolean } {
  const [events, setEvents] = useState<ProgressEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const url = `${apiBaseUrl}/api/recon/jobs/${jobId}/events`;
    const es = new EventSource(url, { withCredentials: true });

    es.addEventListener('open', () => setConnected(true));
    es.addEventListener('error', () => setConnected(false));

    es.addEventListener('progress', (raw) => {
      try {
        const evt = JSON.parse((raw as MessageEvent).data) as ProgressEvent;
        setEvents(prev => [...prev, evt]);
        if (evt.phase === 'job.complete' || evt.phase === 'job.failed') {
          setDone(true);
          onTerminal?.();
        }
      } catch {
        // ignore malformed payloads
      }
    });

    es.addEventListener('end', () => {
      setDone(true);
      es.close();
    });

    return () => { es.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, apiBaseUrl]);

  return { events, connected, done };
}
